import AddOrUpdateProjectSection from "@/src/components/spaces_components/projects/AddOrUpdateProjectSection.vue";
import ProjectItem from "@/src/components/spaces_components/projects/ProjectItem.vue";
import DashboardItem from "@/src/components/spaces_components/dashboards/DashboardItem.vue";
import NoteItem from "@/src/components/spaces_components/notes/NoteItem.vue";
import NoteTreeItem from "@/src/components/spaces_components/notes/NoteTreeItem.vue";
import RepositoryItem from "@/src/components/spaces_components/repositories/RepositoryItem.vue";
import QASuiteItem from "@/src/components/spaces_components/qa/QASuiteItem.vue";
import ServiceItem from "@/src/components/spaces_components/error-logging/ServiceItem.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import UsersTeamsSelectDropdown from "@/src/components/view_components/UsersTeamsSelectDropdown.vue";
import {
  buildNoteSlugId,
  parseNoteIdFromSlug,
  buildNoteTree,
  findNotePath,
} from "@/src/constants/noteConstants.js";
import { errorTrackingEvents } from "@/src/constants/errorTrackingConstants.js";

export const spaceServiceTypes = {
  PROJECTS: "projects",
  ANALYTICS: "analytics",
  NOTES: "notes",
  REPOSITORY: "repository",
  QA: "qa",
  ERROR_LOGGING: "error_logging",
};

export const spaceRelationTypes = {
  PROJECTS: "projects",
  DASHBOARDS: "dashboards",
  NOTES: "notes",
  REPOSITORY: "repository",
  QA_SUITES: "qa_suites",
  ERROR_LOGGING_SERVICES: "error_logging_services",
};

export const spaceServiceConfig = {
  [spaceServiceTypes.PROJECTS]: {
    relationType: spaceRelationTypes.PROJECTS,
    itemComponent: ProjectItem,
    itemIcon: imageNames.PANEL_PROJECT,
    displayName: {
      singular: "Project",
      plural: "Projects",
    },
    addButtonText: "Add Project",
    events: {
      manipulated: "project-manipulated",
      created: "created",
      updated: "updated",
      deleted: "deleted",
    },
    storage: {
      spaceKey: "tasks_selected_space_id",
      itemKey: "tasks_selected_project_id",
    },
    navigation: {
      route: "/spaces/projects/tasks",
      queryParams: (space, item) => ({
        space_id: space?.id,
        project_id: item?.id,
      }),
      parseFromSearch: { itemKey: "project_id", spaceKey: "space_id" },
    },
    specialItem: {
      enabled: true,
      label: "All tasks",
      icon: imageNames.REORDER_ICON,
      storageKey: "tasks_all_tasks_view",
      identifier: "all_tasks",
    },
  },
  [spaceServiceTypes.ANALYTICS]: {
    relationType: spaceRelationTypes.DASHBOARDS,
    itemComponent: DashboardItem,
    itemIcon: imageNames.PANEL_ANALYTICS,
    displayName: {
      singular: "Dashboard",
      plural: "Dashboards",
    },
    addButtonText: "Add Dashboard",
    events: {
      manipulated: "dashboard-manipulated",
      created: "created",
      updated: "updated",
      deleted: "deleted",
    },
    storage: {
      spaceKey: "analytics_selected_space_id",
      itemKey: "analytics_selected_dashboard_id",
    },
    navigation: {
      route: "/analytics/dashboard",
      routeParam: "dashboard_id",
    },
    useAnalyticsStore: true,
  },
  [spaceServiceTypes.NOTES]: {
    relationType: spaceRelationTypes.NOTES,
    itemComponent: NoteTreeItem,
    transformItems: (items) => buildNoteTree(items),
    lazyLoadItems: true,
    itemIcon: imageNames.PANEL_NOTES,
    displayName: {
      singular: "Note",
      plural: "Notes",
    },
    addButtonText: "Add Note",
    events: {
      manipulated: "note-manipulated",
      created: "created",
      updated: "updated",
      deleted: "deleted",
    },
    storage: {
      spaceKey: "notes_selected_space_id",
      itemKey: "notes_selected_note_id",
    },
    navigation: {
      route: "/projects/notes",
      routeName: "note-detail",
      buildRouteParams: (item) => ({ noteSlugId: buildNoteSlugId(item) }),
      parseRouteItemId: (params) => parseNoteIdFromSlug(params.noteSlugId),
      updateItemUrl: (router, item) => {
        if (!item?.id) return;
        try {
          const resolved = router.resolve({
            name: "note-detail",
            params: { noteSlugId: buildNoteSlugId(item) },
          });
          const href = resolved?.href || resolved?.fullPath || String(resolved);
          if (href) window.history.replaceState({}, "", href);
        } catch (e) {
          console.error("updateItemUrl error:", e);
        }
      },
      resolveItemById: async (itemId, transformItems) => {
        const [{ default: ProjectService }, { default: SpaceService }] = await Promise.all([
          import("@/src/api/ProjectService.js"),
          import("@/src/api/SpaceService.js"),
        ]);
        const noteRes = await ProjectService.notes.fetchNoteByID(itemId);
        if (!noteRes?.success || !noteRes?.note) return null;
        const note = noteRes.note;
        const spaceId = note.entity_id;
        const [spaceRes, notesRes] = await Promise.all([
          SpaceService.fetchSpaceByID(spaceId),
          ProjectService.notes.fetchNotes({ entity_id: spaceId, entity_type: "space", page_size: 1000 }),
        ]);
        const spaceData = spaceRes?.data || spaceRes;
        if (!spaceData?.id) return null;
        const flatNotes = notesRes?.notes || notesRes?.data?.notes || [];
        const items = transformItems ? transformItems(flatNotes) : flatNotes;
        const space = { ...spaceData, items };
        const path = findNotePath(items, itemId);
        const item = path ? path[path.length - 1] : { ...note, children: [] };
        return { space, item };
      },
    },
    defaultSpaces: [
      { name: "Private", visibility_type: "private", useCurrentUser: true },
      { name: "Public", visibility_type: "public", useCurrentUser: false },
    ],
  },
  [spaceServiceTypes.REPOSITORY]: {
    relationType: spaceRelationTypes.REPOSITORY,
    itemComponent: RepositoryItem,
    itemIcon: imageNames.PANEL_REPOSITORY,
    displayName: {
      singular: "Repository",
      plural: "Repositories",
    },
    addButtonText: "Add Repository",
    events: {
      manipulated: "repository-manipulated",
      created: "created",
      updated: "updated",
      deleted: "deleted",
    },
    storage: {
      spaceKey: "repository_selected_space_id",
      itemKey: "repository_selected_repo_id",
    },
    navigation: {
      route: "/developer_tools/codebase/repository",
      routeParam: "repo_id",
      routeName: "code-repository",
      buildRouteParams: (project) => ({ repo_id: project.id }),
      updateItemUrl: (router, item) => {
        if (!item?.id) return;
        try {
          const resolved = router.resolve({
            name: "code-repository",
            params: { repo_id: item.id },
          });
          const href = resolved?.href || resolved?.fullPath || String(resolved);
          if (href) window.history.replaceState({}, "", href);
        } catch (e) {
          console.error("updateItemUrl error:", e);
        }
      },
    },
  },
  [spaceServiceTypes.QA]: {
    relationType: spaceRelationTypes.QA_SUITES,
    itemComponent: QASuiteItem,
    itemIcon: imageNames.PANEL_REPOSITORY,
    displayName: {
      singular: "Suite",
      plural: "Suites",
    },
    addButtonText: "Add Suite",
    events: {
      manipulated: "qa-suite-manipulated",
      created: "created",
      updated: "updated",
      deleted: "deleted",
    },
    storage: {
      spaceKey: "qa_selected_space_id",
      itemKey: "qa_selected_suite_id",
    },
    navigation: {
      route: "/developer_tools/ai-qa",
      routeName: "qa-suite-content",
      buildRouteParams: (item) => ({ suiteId: String(item?.id) }),
      parseRouteItemId: (params) => parseInt(params.suiteId),
    },
  },
  [spaceServiceTypes.ERROR_LOGGING]: {
    relationType: spaceRelationTypes.ERROR_LOGGING_SERVICES,
    itemComponent: ServiceItem,
    itemIcon: imageNames.PANEL_REPOSITORY,
    displayName: {
      singular: "Service",
      plural: "Services",
    },
    addButtonText: "Add Service",
    events: {
      manipulated: errorTrackingEvents.SERVICE_MANIPULATED,
      created: errorTrackingEvents.CREATED,
      updated: errorTrackingEvents.UPDATED,
      deleted: errorTrackingEvents.DELETED,
    },
    storage: {
      spaceKey: "error_logging_selected_space_id",
      itemKey: "error_logging_selected_service_id",
    },
    navigation: {
      routeName: "error-logging-service",
      buildRouteParams: (item) => ({ serviceId: String(item?.id) }),
      parseRouteItemId: (params) => parseInt(params.serviceId),
    },
  },
};

export const getSpaceServiceConfig = (serviceType) => {
  return spaceServiceConfig[serviceType] || null;
};

export const spacesAddNewTypes = {
  PROJECTS: spaceRelationTypes.PROJECTS,
};

export const spacesAddNewTypeComponentMapping = {
  [spaceRelationTypes.PROJECTS]: AddOrUpdateProjectSection,
};

export const spaceEvents = {
  CREATED: "created",
  UPDATED: "updated",
  DELETED: "deleted",
};

export const getSpaceItemActions = (imageNames, handlers) => [
  {
    id: "more_options",
    title: "More options",
    icon: imageNames.MORE_ICON_GREY,
    size: 14,
    tooltip: true,
    options: [
      {
        id: "edit",
        title: "Edit",
        icon: imageNames.EDIT,
        size: 14,
        action: handlers.onEdit,
      },
      {
        id: "delete",
        title: "Delete",
        icon: imageNames.DELETE_GREY,
        size: 14,
        action: handlers.onDelete,
      },
    ],
  },
  {
    id: "add_new",
    title: "Add new",
    icon: imageNames.PLUS_GREY_THIN,
    size: 14,
    tooltip: true,
    options: [
      {
        id: "project",
        title: "Project",
        icon: imageNames.PANEL_PROJECT,
        size: 14,
        action: handlers.onAddProject,
      },
    ],
  },
];

export const spaceVisibilityOptions = {
  PUBLIC: "public",
  SHARED: "shared",
  PRIVATE: "private",
};

export const spaceVisibilityOptionDetails = [
  {
    value: spaceVisibilityOptions.PUBLIC,
    label: "Public",
    description: "Everybody in the workspace can access this Space",
  },
  {
    value: spaceVisibilityOptions.SHARED,
    label: "Shared",
    description:
      "You and only people you add will be able to access this Space",
    component: UsersTeamsSelectDropdown,
  },
  {
    value: spaceVisibilityOptions.PRIVATE,
    label: "Private",
    description: "You only will be able to access this Space",
    icon: imageNames.LOCK_GREY,
  },
];
